import { createElement, type ReactNode } from 'react';
import { parseFragment, type DefaultTreeAdapterTypes } from 'parse5';
import { cn } from '../../lib/utils';

const HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
const listingTags = new Set(['p', 'br', 'strong', 'em', 's', 'ul', 'ol', 'li']);
const droppedSubtrees = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template']);
const orderedListClasses = {
  '1': 'list-decimal',
  a: 'list-[lower-alpha]',
  A: 'list-[upper-alpha]',
  i: 'list-[lower-roman]',
  I: 'list-[upper-roman]',
} as const;
type OrderedListType = keyof typeof orderedListClasses;

function attribute(element: DefaultTreeAdapterTypes.Element, name: string): string | undefined {
  return element.attrs.find(value => value.name === name && !value.namespace && !value.prefix)?.value;
}

function listStart(element: DefaultTreeAdapterTypes.Element): number | undefined {
  const value = attribute(element, 'start')?.replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/g, '');
  if (!value || !/^[+-]?\d+$/.test(value)) return undefined;
  const start = Number(value);
  return Number.isInteger(start) && start >= -2147483648 && start <= 2147483647 ? start : undefined;
}

function listType(element: DefaultTreeAdapterTypes.Element): OrderedListType | undefined {
  const value = attribute(element, 'type');
  return value && Object.hasOwn(orderedListClasses, value) ? value as OrderedListType : undefined;
}

// Whitespace between blocks (the root, ul, ol) is source formatting; under pre-wrap it would render as blank lines.
function renderNodes(nodes: DefaultTreeAdapterTypes.ChildNode[], parentKey: string, blockParent = false): ReactNode[] {
  return nodes.map((node, index) => {
    if ('value' in node) return blockParent && !node.value.trim() ? null : node.value;
    if (!('tagName' in node) || node.namespaceURI !== HTML_NAMESPACE || droppedSubtrees.has(node.tagName) || !listingTags.has(node.tagName)) return null;
    const key = `${parentKey}-${index}`;
    if (node.tagName === 'br') return <br key={key} />;
    const children = renderNodes(node.childNodes, key, node.tagName === 'ul' || node.tagName === 'ol');
    if (node.tagName === 'ol') {
      const type = listType(node);
      return <ol key={key} start={listStart(node)} type={type} className={orderedListClasses[type ?? '1']}>{children}</ol>;
    }
    return createElement(node.tagName, { key }, children);
  });
}

/** The canonical editor vocabulary is parsed identically during SSR and in the browser. */
export function ListingDescription({ value, className }: { value: string; className?: string }) {
  const content = /<\/?[a-z][^>]*>/i.test(value)
    ? renderNodes(parseFragment(value).childNodes, 'description', true)
    : <p>{value}</p>;
  return <div className={cn('whitespace-pre-wrap break-words [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:pl-6', className)}>{content}</div>;
}
